import { tangentAtS } from './bezier-geometry';
import type { Guide, GuideId } from './guide-types';
import { isGuideSnap, type Base, type GuideSnap, type TbcPoint, type TopSnap } from './tbc-types';

/** Narrow a base/top snap binding to a guide snap, dropping parent-top
 *  attachments (which aren't on a guide and so have no tangent). All guide
 *  snap kinds — line, circle, and bezier — are kept. */
export function guideSnapOf(snap: Base['snap']): GuideSnap | null {
    return isGuideSnap(snap) ? snap : null;
}

/** Unit tangent vector at a guide snap point. Line guides have a constant
 *  tangent (the line direction); concentric-circle snaps yield the tangent
 *  at the snap angle; bezier snaps yield the curve tangent at the snap's arc
 *  fraction. Returns null if the snap's guide kind doesn't match the snap kind
 *  (defensive — engine invariants should prevent this) or the guide is
 *  degenerate. The tangent is used to orient per-leg symbol adornments along
 *  the guide rather than perpendicular to the leg. Exported for unit testing. */
export function guideTangentAt(guide: Guide, snap: GuideSnap): { x: number; y: number } | null {
    if (guide.type === 'line' && snap.kind === 'guide-line') {
        const dx = guide.end.x - guide.start.x;
        const dy = guide.end.y - guide.start.y;
        const len = Math.hypot(dx, dy);
        if (len === 0) return null;
        return { x: dx / len, y: dy / len };
    }
    if ((guide.type === 'concentric-circles' || guide.type === 'concentric-arcs')
        && snap.kind === 'guide-circle') {
        return { x: -Math.sin(snap.angle), y: Math.cos(snap.angle) };
    }
    if (guide.type === 'bezier-curve' && snap.kind === 'guide-bezier') {
        return tangentAtS(guide, snap.s);
    }
    return null;
}

/** Look up the unit tangent at a leg's tip-side guide snap. Returns null
 *  when there's no snap or the guide has gone away. The caller decides
 *  whether to apply it (asymmetric `+` legs use it for the crossing bar). */
export function tipGuideTangent(
    tipSnap: GuideSnap | null,
    guides: ReadonlyMap<GuideId, Guide>,
): { x: number; y: number } | null {
    if (tipSnap == null) return null;
    const guide = guides.get(tipSnap.guideId);
    if (guide == null) return null;
    return guideTangentAt(guide, tipSnap);
}

/** Unit tangent at a stitch's top guide snap — the outward tip of a symmetric
 *  stitch — or null when the top isn't guide-snapped or its guide is gone. The
 *  symmetric-stitch analogue of `leg0TipTangent`'s asymmetric fanning tip; used
 *  to orient the double-crochet top-cap bar along the guide at the top. */
export function topTipTangent(
    tops: { snap: TopSnap | null }[],
    guides: ReadonlyMap<GuideId, Guide> | undefined,
): TbcPoint | null {
    if (guides == null) return null;
    return tipGuideTangent(guideSnapOf(tops[0].snap), guides);
}

/** Unit tangent at leg 0's unique-end tip for a single-crochet `+` asymmetric
 *  stitch, or null when it doesn't apply (no guides, not a `+`, symmetric, or
 *  the tip isn't guide-snapped). Leg 0 is base→tops[0] for an increase (tip =
 *  tops[0]) and bases[0]→top for a decrease (tip = bases[0]). Shared by the
 *  sizing-handle renderer (pixi) and the engine's hit-test / drag so the
 *  handle pair tracks the rendered crossing bar even when it's tangent-rotated. */
export function leg0TipTangent(
    tops: { snap: TopSnap | null }[],
    bases: { snap: Base['snap'] }[],
    guides: ReadonlyMap<GuideId, Guide> | undefined,
): TbcPoint | null {
    if (guides == null) return null;
    // Mirror the renderer's asymmetricKind precedence: decrease (bases > 1) is
    // checked before increase (tops > 1) so the tip side matches the drawn leg.
    if (bases.length > 1) return tipGuideTangent(guideSnapOf(bases[0].snap), guides);
    if (tops.length > 1) return tipGuideTangent(guideSnapOf(tops[0].snap), guides);
    return null;
}
